import { ChevronDown } from 'lucide-react';
import { MutableRefObject, useCallback, useRef, useState } from 'react';

import { clsx } from 'clsx';
import { useOnClickOutside } from 'usehooks-ts';

import { ICON_DEFAULT } from '@shared/constants/colors';
import { OptionType } from '@shared/types/global';
import { CloseIcon } from '@shared/ui/CloseIcon';

import { CheckboxListItem } from '../../CheckboxListItem/CheckboxListItem';
import cls from './SelectWithCheckbox.module.scss';

type CheckboxSelectProps = {
  options: OptionType[];
  onChange: (value: OptionType[]) => void;
  className?: string;
  label: string;
};

export const SelectWithCheckbox = ({ options, onChange, className, label }: CheckboxSelectProps) => {
  const wrapRef = useRef() as MutableRefObject<HTMLDivElement>;
  const [open, setOpen] = useState(false);
  const activeLength = options.filter((el) => el.active).length;

  const toggleSelect = () => {
    setOpen(!open);
  };

  const onChangeHandler = useCallback(
    (value: boolean, label: string) => {
      onChange(options.map((el) => (el.label === label ? { ...el, active: value } : el)));
    },
    [onChange, options]
  );

  const removeAll = useCallback(() => {
    onChange(options.map((el) => ({ ...el, active: false })));
  }, [options, onChange]);

  const closeSelect = () => {
    setOpen(false);
  };

  useOnClickOutside(wrapRef, closeSelect);

  const modes = {
    [cls.open]: open
  };

  return (
    <div
      data-testid={'filter-select'}
      ref={wrapRef}
      className={clsx(cls.wrap, className, modes)}
    >
      <div className={cls.inner}>
        <div
          data-testid={'filter-select-trigger'}
          onClick={toggleSelect}
          className={cls.currentValue}
        >
          <span>{label}</span>
          <ChevronDown
            className={cls.trigger}
            size={20}
            color={ICON_DEFAULT}
          />

          {activeLength > 0 && (
            <CloseIcon
              testId={'remove-all-button'}
              size={18}
              className={cls.removeAll}
              onClick={removeAll}
            />
          )}
        </div>
        <div
          data-testid={'filter-select-options'}
          className={cls.options}
        >
          <div className={cls.scroll}>
            {options.map((el, i) => (
              <div
                key={i}
                className={cls.option}
              >
                <CheckboxListItem
                  onChange={onChangeHandler}
                  label={el.label}
                  active={el.active}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
